import { generatePath } from 'react-router-dom';
import { Button } from '@accure/ui';
import { ChevronRight } from 'lucide-react';

import { Link } from '@/components/ui/link';
import type { Alert } from '@/entities/alert';
import { AlertLevel } from '@/entities/alert-level';
import { PageUrlKey } from '@/entities/page-url-key';
import { useCurrentAsset } from '@/hooks/use-current-asset';
import { useMe } from '@/hooks/use-me';
import { GetLogCountQuery } from '@/system-logs/api/queries/get-log-count';
import { SystemLogsInternalIcon } from '@/system-logs/view/components/system-logs-internal-icon';

import { LogCountBadge } from './log-count-badge';

/**
 * SystemLogsLinkButton
 * - Shows a button linking to system logs, with a subtle log count badge.
 * - Uses a mock log count for now; replace with real hook when available.
 */
type SystemLogsLinkButtonProps = {
  alert?: Alert;
};

export function SystemLogsLinkButton({ alert }: SystemLogsLinkButtonProps) {
  const me = useMe();
  const asset = useCurrentAsset();

  const { data: { count: logCount } = { count: 0 }, isLoading } =
    GetLogCountQuery.useQuery({
      siteAccureId: asset.accureId,
      unitAccureId: alert?.id,
    });

  // Build asset-scoped system logs URL with query params based on alert level
  const topType = me.config.type === 'mobility' ? 'vehicle' : 'asset';
  const siteId = asset.assetId ?? '';

  const toBase = siteId
    ? generatePath(`/:topType/:siteId/${PageUrlKey.SYSTEM_LOGS}`, {
        topType,
        siteId,
      })
    : `/${PageUrlKey.SYSTEM_LOGS}`;

  const params = new URLSearchParams();
  // Set domain from alert level
  if (alert?.level === AlertLevel.PCS) {
    params.set('domain', 'pcs');
  } else if (alert?.level) {
    // Treat all non-PCS levels as battery domain for now
    params.set('domain', 'battery');
  }
  // Map alert level to supported filter key
  const levelKeyByAlertLevel: Partial<Record<AlertLevel, string>> = {
    [AlertLevel.SYSTEM]: 'system',
    [AlertLevel.ARRAY]: 'array',
    [AlertLevel.NODE]: 'node',
    [AlertLevel.CONTAINER]: 'container',
    [AlertLevel.BANK]: 'bank',
    [AlertLevel.PACK]: 'pack',
    [AlertLevel.MODULE]: 'module',
    [AlertLevel.PCS]: 'pcs',
    // 'cell' deep filtering not yet supported in URL schema
  };
  const levelKey = alert
    ? levelKeyByAlertLevel[alert.level as AlertLevel]
    : undefined;
  if (levelKey && alert?.id) {
    params.set(levelKey, alert.id);
  }

  return (
    <Link to={{ pathname: toBase, search: params.toString() }}>
      <Button
        size="lg"
        variant="secondary"
        data-testid="alert-go-to-system-logs-button"
        className="relative"
      >
        <SystemLogsInternalIcon featureFlag="systemLogs" className="mr-1" />
        View system logs
        <LogCountBadge
          count={logCount}
          isLoading={isLoading}
          className="ml-2"
        />
        <ChevronRight />
      </Button>
    </Link>
  );
}
